import { Component, ElementRef, Inject, OnInit, ViewChild } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog"
import { MatButtonModule } from "@angular/material/button"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatInputModule } from "@angular/material/input"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { RECORDING_MESSAGE_MAX_CHARS, Recording, RecordingStationMessage } from "../../models/recording.model"
import { ProfileService } from "../../services/profile.service"
import { recordingApiError, RecordingService } from "../../services/recording.service"
import { UploadService } from "../../services/upload.service"
import { isMp3File, MP3_FILE_ERROR, UNREADABLE_MP3_ERROR } from "../../utils/audio-file"
import { MAX_SPOT_AUDIO_SECONDS, exceedsMaxSpotAudioSeconds } from "../../utils/spot-length"

export interface RecordingFeedbackDialogData {
  recording: Recording
}

export interface RecordingFeedbackDialogResult {
  submitted?: boolean
  stationName?: string
}

/** Same file picker constraint as the audio upload page. */
const CORRECTED_AUDIO_ACCEPT = "audio/mpeg,audio/mp3"

@Component({
  selector: "app-recording-feedback-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: "./recording-feedback-dialog.component.html",
  styleUrls: ["./recording-feedback-dialog.component.css"],
})
export class RecordingFeedbackDialogComponent implements OnInit {
  messages: RecordingStationMessage[] = []
  loading = true
  sending = false
  submitting = false
  uploading = false
  replyText = ""
  error = ""
  selectedFileName = ""
  isUploader = false
  readonly maxChars = RECORDING_MESSAGE_MAX_CHARS
  readonly audioAccept = CORRECTED_AUDIO_ACCEPT
  @ViewChild("dialogBody") private dialogBody?: ElementRef<HTMLElement>

  private audioTmpKey: string | null = null
  private audioLength: number | null = null
  private readonly currentUserId: number | null

  constructor(
    private dialogRef: MatDialogRef<RecordingFeedbackDialogComponent, RecordingFeedbackDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: RecordingFeedbackDialogData,
    private recordingService: RecordingService,
    private uploadService: UploadService,
    private profile: ProfileService,
  ) {
    const currentUserId = this.profile.getUserId()
    this.currentUserId = currentUserId != null ? Number(currentUserId) : null
    const uploaderId = data.recording?.uploaderid
    this.isUploader =
      currentUserId != null && uploaderId != null && Number(currentUserId) === Number(uploaderId)
  }

  ngOnInit(): void {
    this.loadMessages()
  }

  get recordingName(): string {
    return this.data.recording?.name || "Recording"
  }

  get stationLabel(): string {
    return this.data.recording?.station_name || (this.stationId != null ? `Station #${this.stationId}` : "this station")
  }

  get canReply(): boolean {
    return !!this.replyText.trim() && this.replyText.trim().length <= this.maxChars && !this.sending && !this.loading
  }

  /** Uploader, declined at this station, and not already replaced by a corrected recording. */
  get canOfferCorrected(): boolean {
    return (
      this.isUploader &&
      this.data.recording?.approval_status === 2 &&
      !this.data.recording?.is_superseded &&
      this.recordingId != null &&
      this.stationId != null
    )
  }

  get carriedFromId(): number | null {
    const id = Number(this.data.recording?.resubmitted_from_recording_id)
    return Number.isFinite(id) && id > 0 ? id : null
  }

  get canSubmitCorrected(): boolean {
    return (
      this.canOfferCorrected &&
      !!this.audioTmpKey &&
      this.audioLength != null &&
      this.audioLength >= 1 &&
      !exceedsMaxSpotAudioSeconds(this.audioLength) &&
      !this.uploading &&
      !this.submitting
    )
  }

  /**
   * Every station decline is a "Reason for decline", including a repeat decline of a
   * corrected recording (message_type). Messages from before message_type existed fall
   * back to "the thread's first station message is the reason".
   */
  messageLabel(msg: RecordingStationMessage, index: number): string {
    const fromUploader = msg.sender_role === "user"
    if (msg.message_type === "decline_reason") return "Reason for decline"
    if (!msg.message_type && index === 0 && !fromUploader) return "Reason for decline"
    if (fromUploader) return this.isUploader ? "Your reply" : "Uploader reply"
    if (this.isUploader) return "Station reply"
    const mine = this.currentUserId != null && msg.sender_user_id != null
      && Number(msg.sender_user_id) === Number(this.currentUserId)
    return mine ? "Your reply" : "Station reply"
  }

  isFromUploader(msg: RecordingStationMessage): boolean {
    return msg.sender_role === "user"
  }

  loadMessages(): void {
    const recordingId = this.recordingId
    const stationId = this.stationId
    if (recordingId == null || stationId == null) {
      this.loading = false
      this.error = "This recording is not tied to a station, so there is no feedback thread to show."
      return
    }

    this.loading = true
    this.recordingService.listMessages(recordingId, stationId).subscribe({
      next: (res) => {
        this.messages = Array.isArray(res?.messages) ? res.messages : []
        this.loading = false
        this.showDeclineReason()
      },
      error: (err) => {
        this.loading = false
        this.messages = []
        this.error = recordingApiError(err, "Unable to load feedback for this recording.")
      },
    })
  }

  sendReply(): void {
    const text = this.replyText.trim()
    const recordingId = this.recordingId
    const stationId = this.stationId
    if (!text || this.sending || recordingId == null || stationId == null) return
    if (text.length > this.maxChars) {
      this.error = `message_text must be ${this.maxChars} characters or fewer`
      return
    }

    this.sending = true
    this.error = ""
    this.recordingService.postMessage(recordingId, stationId, text).subscribe({
      next: (res) => {
        this.sending = false
        this.replyText = ""
        const created = res?.message
        if (created && typeof created === "object" && created.message_text) {
          this.messages = [...this.messages, created]
        } else {
          this.loadMessages()
        }
      },
      error: (err) => {
        this.sending = false
        this.error = recordingApiError(err, "Failed to send reply.")
      },
    })
  }

  /** True when a known length is outside 1–65 seconds. Unknown length is not an error yet. */
  private applyCorrectedLengthError(): boolean {
    if (this.audioLength == null) return false
    if (this.audioLength < 1) {
      this.error = "Audio is too short (minimum 1 second)."
      this.audioTmpKey = null
      return true
    }
    if (exceedsMaxSpotAudioSeconds(this.audioLength)) {
      this.error = `Audio is too long (maximum ${MAX_SPOT_AUDIO_SECONDS} seconds).`
      this.audioTmpKey = null
      return true
    }
    return false
  }

  onAudioSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ""
    if (!file) return

    if (!isMp3File(file)) {
      this.error = MP3_FILE_ERROR
      this.selectedFileName = ""
      this.audioTmpKey = null
      this.audioLength = null
      return
    }

    this.error = ""
    this.selectedFileName = file.name
    this.uploading = true
    this.audioTmpKey = null
    this.audioLength = null

    const objectUrl = URL.createObjectURL(file)
    const audio = new Audio()
    audio.preload = "metadata"
    audio.src = objectUrl
    audio.onloadedmetadata = () => {
      this.audioLength = Number.isFinite(audio.duration) ? audio.duration : null
      URL.revokeObjectURL(objectUrl)
      if (this.audioLength == null) {
        this.audioTmpKey = null
        this.selectedFileName = ""
        this.uploading = false
        this.error = UNREADABLE_MP3_ERROR
        return
      }
      this.applyCorrectedLengthError()
    }
    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      this.audioLength = null
      this.audioTmpKey = null
      this.selectedFileName = ""
      this.uploading = false
      this.error = UNREADABLE_MP3_ERROR
    }

    this.uploadService.uploadFile(file, "recordings").subscribe({
      next: (result) => {
        if (this.applyCorrectedLengthError()) {
          this.uploading = false
          return
        }
        this.audioTmpKey = result.tmp_key
        this.uploading = false
      },
      error: (err) => {
        this.uploading = false
        this.audioTmpKey = null
        this.selectedFileName = ""
        this.error = recordingApiError(err, "Failed to upload audio file. Please try again.")
      },
    })
  }

  submitCorrected(): void {
    if (!this.canSubmitCorrected || this.audioTmpKey == null || this.audioLength == null) return
    const recordingId = this.recordingId
    const stationId = this.stationId
    if (recordingId == null || stationId == null) return

    this.submitting = true
    this.error = ""
    this.dialogRef.disableClose = true

    this.recordingService
      .resubmitNew(recordingId, stationId, this.audioTmpKey, this.audioLength)
      .subscribe({
        next: () => {
          this.dialogRef.disableClose = false
          this.dialogRef.close({ submitted: true, stationName: this.stationLabel })
        },
        error: (err) => {
          this.submitting = false
          this.dialogRef.disableClose = false
          this.error = recordingApiError(err, "Unable to submit this corrected recording.")
        },
      })
  }

  private showDeclineReason(): void {
    setTimeout(() => {
      const body = this.dialogBody?.nativeElement
      if (body) body.scrollTop = 0
    })
  }

  close(): void {
    if (this.sending || this.submitting) return
    this.dialogRef.close()
  }

  private get recordingId(): number | null {
    const raw = this.data.recording?.id ?? this.data.recording?.recording_id
    const id = Number(raw)
    return Number.isFinite(id) && id > 0 ? id : null
  }

  private get stationId(): number | null {
    const id = Number(this.data.recording?.station_id)
    return Number.isFinite(id) && id > 0 ? id : null
  }
}
